import { observer } from "mobx-react-lite";
import { FC, useMemo } from "react";
import { AppRouter } from "@/app/router/AppRouter";
import { useInitializePublicAppStore } from "@/store";
import { PublicAppStoreContext } from "@/store/utils/context";
import { GlobalToastContainer } from "@/components/toast";
import { MdsAppStyleWrapper, MdsGlobalProvider } from "@/components/styling";
import { ThemeProvider } from "@emotion/react";
import { ThemeContextProvider, useTheme } from "@/mds";
import { FindInPageOverlay } from "@/components/overlay/find-in-page/FindInPageOverlay";
import { AppVersionHandling } from "@/app/core/AppVersionHandling";
import { PageDragAndDropProvider } from "@/components/drag-and-drop";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { DesktopMenuOverlay } from "@/components/desktop/DesktopMenuOverlay";
import { localStorageModule } from "@/modules/local-storage";

const ThemedApp: FC = observer(() => {
  const { theme } = useTheme();

  return (
    <ThemeProvider theme={theme}>
      <AppRouter />
      <DesktopMenuOverlay />
      <FindInPageOverlay />
      <GlobalToastContainer />
      <AppVersionHandling />
    </ThemeProvider>
  );
});

export const AppCore: FC = observer(() => {
  const queryClient = useMemo(() => new QueryClient(), []);

  const state = useInitializePublicAppStore();

  if (state.loading) {
    /**
     * Loading.
     * @todo - Introduce some form of loading-interstitial here.
     */
    return <div />;
  }

  if (state.error || !state.data) {
    return <div>Something went wrong.</div>;
  }

  return (
    <PublicAppStoreContext.Provider value={state.data}>
      <QueryClientProvider client={queryClient}>
        <MdsGlobalProvider>
          <PageDragAndDropProvider>
            <MdsAppStyleWrapper>
              <ThemeContextProvider
                getPersistedTheme={() => localStorageModule.readThemeType()}
                persistTheme={theme => localStorageModule.writeThemeType(theme)}
              >
                <ThemedApp />
              </ThemeContextProvider>
            </MdsAppStyleWrapper>
          </PageDragAndDropProvider>
        </MdsGlobalProvider>
      </QueryClientProvider>
    </PublicAppStoreContext.Provider>
  );
});
